import { useEffect } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Input } from '@app/pages/public/components';
import { Namespace } from '@data';
import { Button, AntdForm, AntdTypography, AntdTooltip } from '@ui';
import { useSignUpTracking } from '../../hooks';
import '../../sign-up.css';
import { useCreateTeam } from './hooks';

const { Text, Title } = AntdTypography;

export const TeamForm = () => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const isMutating = !!useIsMutating();

  const { form, handleCreateTeam, initialValues } = useCreateTeam();

  const { trackCreateTeamPageView } = useSignUpTracking();

  useEffect(() => {
    trackCreateTeamPageView();
  }, []);

  return (
    <div className="flex flex-col">
      <Title level={2} className="mb-2 mt-0 md:mt-6">
        {t('sign_up.create_team')}
      </Title>
      <Text className="text-base-800 mb-5 inline-block">
        {t('sign_up.create_team_description')}
      </Text>
      <AntdForm
        name="create-team-form"
        form={form}
        layout="vertical"
        className="sing-up-form mt-2"
        disabled={isMutating}
        autoComplete="off"
        initialValues={initialValues}
        onFinish={handleCreateTeam}
      >
        <AntdForm.Item
          label={t('sign_up.job_title')}
          name="teamRole"
          className="font-semibold"
          rules={[{ required: true, type: 'string', message: t('patient:validations.required') }]}
        >
          <Input className="h-[48px] sm:h-auto" size="large" />
        </AntdForm.Item>

        <AntdForm.Item
          label={
            <span>
              <Text>{t('sign_up.team_name')}</Text>
              <AntdTooltip className="ml-1" title={t('sign_up.team_name_tooltip')}>
                <QuestionCircleOutlined className="text-base-500" />
              </AntdTooltip>
            </span>
          }
          name="name"
          className="font-semibold"
          rules={[{ required: true, type: 'string', message: t('patient:validations.required') }]}
        >
          <Input className="h-[48px] sm:h-auto" size="large" />
        </AntdForm.Item>

        <AntdForm.Item
          label={
            <span>
              <Text>{t('sign_up.research_site')}</Text>
              <AntdTooltip className="ml-1" title={t('sign_up.research_site_tooltip')}>
                <QuestionCircleOutlined className="text-base-500" />
              </AntdTooltip>
            </span>
          }
          name="researchSiteName"
          className="font-semibold"
        >
          <Input className="h-[48px] sm:h-auto" size="large" />
        </AntdForm.Item>
        <AntdForm.Item
          label={
            <span>
              <Text>{t('sign_up.organization')}</Text>
              <AntdTooltip className="ml-1" title={t('sign_up.organization_tooltip')}>
                <QuestionCircleOutlined className="text-base-500" />
              </AntdTooltip>
            </span>
          }
          name="organizationName"
          className="font-semibold"
        >
          <Input className="h-[48px] sm:h-auto" size="large" />
        </AntdForm.Item>
        <div className="flex flex-col w-full gap-4 mt-8">
          <Button
            className="w-full h-10"
            htmlType="submit"
            size="large"
            loading={isMutating}
            type="primary"
            data-testid="submit-button"
          >
            {t('sign_up.create_team')}
          </Button>
        </div>
      </AntdForm>
    </div>
  );
};
